/**
 * Video Recorder Service
 *
 * Client-side video recording using the MediaRecorder API.
 * Sends video chunks over WebSocket to the live-session worker for R2 storage.
 *
 * Key constraints:
 * - 2-second chunk intervals for real-time upload and minimal data loss
 * - 1 Mbps bitrate for balance of quality and size
 * - Chunks must stay under 1 MiB (Durable Object WebSocket limit)
 * - Supports WebM (Chrome/Firefox) and MP4 (Safari) codecs
 */

import { captureException } from '../lib/telemetry/posthog/frontend';
import type { SegmentKind } from '../types';

// Binary message type for video chunks (matches worker)
const VIDEO_MESSAGE_TYPE = 0x02;

export interface VideoRecorderConfig {
  /**
   * Camera stream. Omit for audio-only recordings (audio-only calls): the
   * recorder then captures `audioStream` alone using audio/* MIME types.
   */
  videoStream?: MediaStream;
  /** Mic + AI playback mix. Required for audio-only mode; combined with video otherwise. */
  audioStream?: MediaStream;
  websocket: WebSocket;
  onError: (error: Error) => void;
  onChunkSent: (chunkIndex: number, size: number) => void;
  /**
   * Fired when the underlying MediaRecorder's `onstart` fires, carrying the
   * segment's capture-time anchor.
   *
   * @param kind - 'camera' or 'audio' (a VideoRecorder never records screen)
   * @param clientStartMs - Client Date.now() (epoch ms) at recorder start
   */
  onRecordingStarted?: (kind: SegmentKind, clientStartMs: number) => void;
  onRecordingStopped?: () => void;
}

export class VideoRecorder {
  private recorder: MediaRecorder | null = null;
  private chunkIndex = 0;
  private isRecording = false;
  private actualMimeType: string = '';
  private config: VideoRecorderConfig | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private lastChunkTime = 0;

  /**
   * Chunks that could not be sent because the socket was not OPEN, in arrival
   * order. The worker appends chunks in the order they arrive, so a failed
   * chunk must hold back every later one until it has been resent.
   */
  private pendingChunks: Array<{ blob: Blob; index: number }> = [];

  /** In-progress drain of pendingChunks; concurrent triggers share it. */
  private flushPromise: Promise<void> | null = null;

  /** Chunk sends started by dataavailable events that have not settled yet. */
  private inflightSends = new Set<Promise<void>>();

  /** Resolves the stop() waiter when the MediaRecorder stop event fires. */
  private notifyStopped: (() => void) | null = null;

  /** Limits buffer-overflow telemetry to one capture per outage. */
  private overflowReported = false;

  /**
   * True while restart() is tearing down the old recorder and building a fresh
   * one. setWebSocket() must not start a competing pendingChunks flush during
   * this window (two flush drivers would race the shared buffer), so it only
   * rebinds the socket and returns. See restart() and setWebSocket().
   */
  private isRestarting = false;

  // Codec preference order (try WebM first, fall back to MP4 for Safari)
  private static readonly MIME_TYPES = [
    'video/webm;codecs=vp9,opus',      // Best quality WebM (Chrome, Firefox, Edge)
    'video/webm;codecs=vp8,opus',      // Fallback WebM
    'video/webm',                       // Generic WebM
    'video/mp4;codecs=avc1,mp4a.40.2', // Safari/iOS - H.264 AAC
    'video/mp4;codecs=h264,aac',       // Safari/iOS fallback
    'video/mp4',                        // Generic MP4
  ];

  // Codec preference for audio-only recordings (audio-only calls).
  // Opus for Chrome/Firefox/Edge; AAC/MP4 for Safari (no Opus support).
  private static readonly AUDIO_MIME_TYPES = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];

  // Use 2-second chunks to minimize data loss on disconnect
  // At 1Mbps, 2s chunks = ~250KB each (well under 1 MiB WebSocket limit)
  private static readonly CHUNK_INTERVAL_MS = 2000;
  private static readonly BITRATE = 1_000_000; // 1 Mbps

  // Maximum chunk size we accept (under 1 MiB DO WebSocket limit)
  private static readonly MAX_CHUNK_SIZE = 900_000; // 900KB safety limit

  // Heartbeat interval for monitoring upload health
  private static readonly HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds

  /**
   * Maximum chunks held for resend while the socket is down (~3 minutes at
   * 2-second chunks, ~22MB at 1 Mbps). Disconnect windows are far shorter;
   * beyond this the oldest chunk is dropped to bound memory.
   */
  static readonly MAX_PENDING_CHUNKS = 90;

  /** Upper bound on waiting for the final dataavailable/stop events at teardown. */
  private static readonly STOP_FLUSH_TIMEOUT_MS = 2000;

  /**
   * Check if the browser supports video recording
   */
  static isSupported(): boolean {
    return typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function';
  }

  /**
   * Detect the best supported MIME type for this browser.
   * @param audioOnly - when true, picks from the audio-only codec list so the
   *   MediaRecorder emits an audio/* container (audio-only calls).
   */
  private static getSupportedMimeType(audioOnly = false): string | undefined {
    const candidates = audioOnly ? VideoRecorder.AUDIO_MIME_TYPES : VideoRecorder.MIME_TYPES;
    for (const mimeType of candidates) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`[VideoRecorder] Using codec: ${mimeType}`);
        return mimeType;
      }
    }
    console.warn('[VideoRecorder] No preferred codec supported, using browser default');
    return undefined;
  }

  /**
   * Get the actual MIME type being used (call after start)
   * Important: Server needs this to set correct Content-Type in R2
   */
  getMimeType(): string {
    return this.actualMimeType;
  }

  /**
   * Get the number of chunks recorded
   */
  getChunkCount(): number {
    return this.chunkIndex;
  }

  /**
   * Get the last chunk timestamp
   */
  getLastChunkTime(): number {
    return this.lastChunkTime;
  }

  /**
   * Check if recording is active
   */
  isActive(): boolean {
    return this.isRecording;
  }

  /**
   * Start video recording
   */
  start(config: VideoRecorderConfig): void {
    if (this.isRecording) {
      console.warn('[VideoRecorder] Already recording');
      return;
    }

    if (!VideoRecorder.isSupported()) {
      config.onError(new Error('MediaRecorder not supported in this browser'));
      return;
    }

    if (!config.videoStream && !config.audioStream) {
      config.onError(new Error('No video or audio stream provided to VideoRecorder.start'));
      return;
    }

    this.config = config;
    this.chunkIndex = 0;
    this.lastChunkTime = 0;
    this.pendingChunks = [];
    this.overflowReported = false;

    this.buildAndStartRecorder();
  }

  /**
   * Build a fresh MediaRecorder from `this.config`, wire its event handlers, and
   * start it with the chunk-interval timeslice. Shared by start() (first launch)
   * and restart() (reconnect segment boundary) so codec detection, audio-only
   * logic, and the timeslice live in one place. Assumes `this.config` is set and
   * the chunkIndex/pendingChunks state has already been reset by the caller.
   */
  private buildAndStartRecorder(): void {
    const config = this.config;
    if (!config) return;

    // Audio-only mode kicks in when no camera stream is supplied. The MIME
    // type list and constructor options change accordingly so MediaRecorder
    // emits an audio/* container (Opus or AAC), not a video container.
    const audioOnly = !config.videoStream;
    const mimeType = VideoRecorder.getSupportedMimeType(audioOnly);

    // Combine video and audio streams into a single MediaStream
    const combinedStream = new MediaStream();

    if (config.videoStream) {
      config.videoStream.getVideoTracks().forEach(track => {
        combinedStream.addTrack(track);
        console.log(`[VideoRecorder] Added video track: ${track.label}`);
      });
    }

    if (config.audioStream) {
      config.audioStream.getAudioTracks().forEach(track => {
        combinedStream.addTrack(track);
        console.log(`[VideoRecorder] Added audio track: ${track.label}`);
      });
    } else if (!audioOnly) {
      console.warn('[VideoRecorder] No audio stream provided - recording video only');
    }

    try {
      // Audio-only mode: don't set videoBitsPerSecond (Chrome treats it as a
      // hint to add a video track). Use audioBitsPerSecond at 64 kbps which
      // is plenty for spoken voice through Opus/AAC.
      this.recorder = audioOnly
        ? new MediaRecorder(combinedStream, {
            mimeType,
            audioBitsPerSecond: 64_000,
          })
        : new MediaRecorder(combinedStream, {
            mimeType,
            videoBitsPerSecond: VideoRecorder.BITRATE,
          });

      // Store the actual MIME type (may differ from requested)
      this.actualMimeType = this.recorder.mimeType;
      console.log(`[VideoRecorder] Actual MIME type: ${this.actualMimeType}`);

      // Capture THIS recorder instance so a late dataavailable from a stale
      // recorder (a Safari-style delayed final flush after restart() already
      // swapped in a new recorder) is ignored. Without this guard, that late
      // chunk would consume the freshly-reset chunkIndex 0 and upload a
      // headerless old-tail chunk as the new segment's header (DEFECT 2).
      const self = this.recorder;
      this.recorder.ondataavailable = (event) => {
        if (this.recorder !== self) return;
        if (event.data.size > 0) {
          // Track the send so stop() can await the final flush before the
          // caller closes the WebSocket (handleChunk never rejects).
          const send = this.handleChunk(event.data);
          this.inflightSends.add(send);
          void send.finally(() => this.inflightSends.delete(send));
        }
      };

      this.recorder.onerror = (event) => {
        // MediaRecorder error events carry the underlying DOMException on
        // `.error`. The DOM lib types `ErrorEvent.error` as `any`, so narrow
        // to just the `.message` we read here.
        const eventError = (event as { error?: { message?: string } }).error;
        const error = new Error(eventError?.message || 'Recording error');
        console.error('[VideoRecorder] Error:', error);
        config.onError(error);
      };

      this.recorder.onstart = () => {
        console.log('[VideoRecorder] Recording started');
        this.isRecording = true;
        // Capture-time anchor: onstart fires before the first media chunk, so
        // this reaches the worker ahead of the segment's upload. A VideoRecorder
        // records camera when a video stream is present, else audio-only.
        const kind: SegmentKind = config.videoStream ? 'camera' : 'audio';
        config.onRecordingStarted?.(kind, Date.now());
        this.startHeartbeat();
      };

      this.recorder.onstop = () => {
        console.log('[VideoRecorder] Recording stopped');
        this.isRecording = false;
        config.onRecordingStopped?.();
        this.stopHeartbeat();
        // The stop event fires after the final dataavailable, so stop() can
        // now wait on the in-flight sends it spawned.
        this.notifyStopped?.();
        this.notifyStopped = null;
      };

      // Start with timeslice to get regular chunks
      this.recorder.start(VideoRecorder.CHUNK_INTERVAL_MS);
    } catch (error) {
      console.error('[VideoRecorder] Failed to start:', error);
      captureException(error instanceof Error ? error : String(error), 'VideoRecorderService.startRecording', { mimeType: mimeType || 'unknown' });
      config.onError(error instanceof Error ? error : new Error('Failed to start recording'));
    }
  }

  /**
   * Rebind chunk uploads to a new WebSocket after a mid-call reconnect.
   * The MediaRecorder keeps running and chunk indexes continue, so the
   * segment resumes on the new socket without overwriting earlier chunks.
   * Chunks buffered while the old socket was down are resent first, in order.
   * No-op if the recorder was never started.
   */
  setWebSocket(websocket: WebSocket): void {
    if (!this.config) return;
    this.config.websocket = websocket;
    // While a restart() is in flight it already owns the flush of the shared
    // pendingChunks (over the socket it was given). Starting a second drain here
    // would race that driver on the same buffer, so only rebind and return.
    if (this.isRestarting) return;
    if (this.pendingChunks.length > 0) {
      void this.flushPendingChunks();
    }
  }

  /**
   * Restart the MediaRecorder at a reconnect segment boundary (DEFECT 2). Unlike
   * setWebSocket (which keeps the running recorder and a monotonic chunkIndex for
   * transient blips), restart() stops the current recorder, drains its final
   * dataavailable + in-flight sends + a final pending-chunk flush over the NEW
   * socket (so the OLD segment's tail lands on its original chunkIndex sequence),
   * then resets chunkIndex to 0 and builds a fresh MediaRecorder. The fresh
   * recorder emits a new WebM/EBML header at chunkIndex 0, aligning with the new
   * server segment.
   *
   * Any old-segment tail the flush could not drain (socket not OPEN) is DROPPED
   * before chunkIndex resets, NOT preserved: the preserved tail keeps its
   * ORIGINAL indices ({0,1,...}), so after the reset the fresh recorder's real
   * header buffers BEHIND it and is sent as a SECOND index 0 - the server then
   * opens the new segment on the headerless old-tail (corrupt .webm = DEFECT 2).
   * The old segment is already finalized server-side, so losing a couple
   * trailing old-segment chunks is far better than corrupting the new segment.
   * (setWebSocket's preserve-and-resend is unaffected: that path keeps the SAME
   * recorder with a monotonic index, so there is no index collision.)
   *
   * Reentrant-safe: a second concurrent restart() (the server can emit a
   * proactive + a reactive restart_recording close together, each calling
   * restart() non-awaited) is a NO-OP - the in-flight restart already produces a
   * fresh recorder at index 0, so a second teardown/rebuild would only leak a
   * running MediaRecorder and drop frames.
   *
   * No-op if the recorder was never started.
   *
   * @param websocket - The live socket the new segment uploads over.
   */
  async restart(websocket: WebSocket): Promise<void> {
    if (!this.config) return;

    // Reentrancy guard (Finding 3): if a restart is already tearing down +
    // rebuilding, a second call is redundant. The in-flight restart produces a
    // fresh recorder at index 0; a second concurrent teardown would capture the
    // not-yet-installed recorder, clobber this.recorder, and leak the first
    // fresh recorder (still running on the tracks, never stopped). Just rebind
    // the socket so the in-flight restart's flush targets the live socket, then
    // bail. A genuinely-later restart (after isRestarting clears) proceeds.
    if (this.isRestarting) {
      this.config.websocket = websocket;
      return;
    }

    // Guard the whole teardown+rebuild so a concurrent setWebSocket() (fired by
    // onReconnected/onConnected on the same recorder) only rebinds the socket
    // and does NOT start a competing pendingChunks flush.
    this.isRestarting = true;
    try {
      // Point at the new socket FIRST so stop()'s final flush of the old segment's
      // tail goes out on the live socket the server is currently reading.
      this.config.websocket = websocket;

      // Retain the OLD recorder so we can fully detach it after stop(). Its
      // onstop is left intact here so stop()'s wait resolves normally on the
      // happy path; only ondataavailable is neutralized below.
      const old = this.recorder;

      // Drain the current recorder fully (final dataavailable + inflight sends +
      // pending-chunk flush) before resetting, so old-segment chunks are never
      // interleaved with the new segment's chunk 0.
      await this.stop();

      // Detach the OLD recorder and drop the reference BEFORE resetting
      // chunkIndex, so a late (Safari-style) dataavailable from it after the
      // stop() timeout cannot consume the reset index 0 and upload a headerless
      // old-tail chunk as the new segment's header (DEFECT 2). The captured-
      // identity guard in buildAndStartRecorder is belt-and-suspenders; nulling
      // the handlers + the reference here closes the window directly.
      if (old) {
        old.ondataavailable = null;
        old.onstop = null;
        old.onstart = null;
        old.onerror = null;
      }
      this.recorder = null;

      // Drop any old-segment tail the stop() flush could NOT drain (socket not
      // OPEN). Preserving it would re-collide its original indices ({0,1,...})
      // with the fresh recorder's reset index 0 and corrupt the new segment
      // (DEFECT 2). The old segment is finalized server-side, so dropping a
      // couple trailing old-segment chunks is the safe trade. Done BEFORE the
      // chunkIndex reset so nothing carries a stale index into the new segment.
      if (this.pendingChunks.length > 0) {
        console.warn(
          `[VideoRecorder] video_restart_tail_dropped: dropping ${this.pendingChunks.length} undrained old-segment chunk(s) to protect the new segment's header at index 0`,
        );
        this.pendingChunks = [];
      }

      // Reset to a clean first-segment state.
      this.chunkIndex = 0;
      this.lastChunkTime = 0;
      this.overflowReported = false;
      this.isRecording = false;

      // Fresh MediaRecorder -> fresh WebM header at chunkIndex 0.
      this.buildAndStartRecorder();
    } finally {
      this.isRestarting = false;
    }
  }

  /**
   * Handle a video chunk from MediaRecorder. A failed send buffers the chunk
   * for resend on reconnect instead of discarding it; once anything is
   * buffered, later chunks queue behind it because the worker appends chunks
   * in arrival order.
   */
  private async handleChunk(blob: Blob): Promise<void> {
    if (!this.config) return;

    const currentIndex = this.chunkIndex++;
    this.lastChunkTime = Date.now();

    // Warn if chunk is too large
    if (blob.size > VideoRecorder.MAX_CHUNK_SIZE) {
      console.warn(
        `[VideoRecorder] Chunk ${currentIndex} is large (${Math.round(blob.size / 1024)}KB), may fail if > 1MB`
      );
    }

    if (this.pendingChunks.length > 0) {
      this.bufferChunk(blob, currentIndex);
      void this.flushPendingChunks();
      return;
    }

    try {
      await this.sendChunk(blob, currentIndex);
      this.config.onChunkSent(currentIndex, blob.size);
    } catch (error) {
      console.warn(`[VideoRecorder] Buffering chunk ${currentIndex} after failed send:`, error);
      this.bufferChunk(blob, currentIndex);
    }
  }

  /**
   * Queue a chunk for resend, evicting the oldest beyond MAX_PENDING_CHUNKS.
   */
  private bufferChunk(blob: Blob, index: number): void {
    this.pendingChunks.push({ blob, index });
    if (this.pendingChunks.length > VideoRecorder.MAX_PENDING_CHUNKS) {
      const dropped = this.pendingChunks.shift()!;
      console.warn(`[VideoRecorder] Pending buffer full, dropping chunk ${dropped.index}`);
      if (!this.overflowReported) {
        this.overflowReported = true;
        captureException(
          new Error('Video chunk buffer overflow; dropping oldest chunk'),
          'VideoRecorderService.bufferChunk',
          { droppedIndex: dropped.index, bufferedChunks: this.pendingChunks.length },
        );
      }
    }
  }

  /**
   * Drain pendingChunks over the current socket, oldest first. Stops at the
   * first failure (the remainder waits for the next rebind). Concurrent
   * triggers share one drain.
   */
  private flushPendingChunks(): Promise<void> {
    this.flushPromise ??= this.drainPendingChunks().finally(() => {
      this.flushPromise = null;
    });
    return this.flushPromise;
  }

  /** See flushPendingChunks; never call directly. */
  private async drainPendingChunks(): Promise<void> {
    while (this.pendingChunks.length > 0) {
      const config = this.config;
      if (!config || config.websocket.readyState !== WebSocket.OPEN) return;
      const next = this.pendingChunks[0];
      try {
        await this.sendChunk(next.blob, next.index);
      } catch {
        return;
      }
      this.pendingChunks.shift();
      this.overflowReported = false;
      config.onChunkSent(next.index, next.blob.size);
    }
  }

  /** Wait until every dataavailable-spawned send has settled. */
  private async waitForInflightSends(): Promise<void> {
    while (this.inflightSends.size > 0) {
      await Promise.all(Array.from(this.inflightSends));
    }
  }

  /**
   * Send a video chunk over WebSocket using binary protocol
   * Format: [type(1)] [index(4)] [mimeTypeLen(1)] [mimeType(n)] [data...]
   */
  private async sendChunk(blob: Blob, index: number): Promise<void> {
    if (!this.config?.websocket || this.config.websocket.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    const buffer = await blob.arrayBuffer();
    const mimeBytes = new TextEncoder().encode(this.actualMimeType);

    // Create header: [type(1)] [index(4)] [mimeTypeLen(1)] [mimeType(n)]
    const headerSize = 6 + mimeBytes.length;
    const message = new Uint8Array(headerSize + buffer.byteLength);
    const view = new DataView(message.buffer);

    // Write header
    message[0] = VIDEO_MESSAGE_TYPE;
    view.setUint32(1, index, true); // little-endian
    message[5] = mimeBytes.length;
    message.set(mimeBytes, 6);

    // Write video data
    message.set(new Uint8Array(buffer), headerSize);

    // Send binary message
    this.config.websocket.send(message.buffer);
  }

  /**
   * Start heartbeat for monitoring upload health
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (!this.config?.websocket || this.config.websocket.readyState !== WebSocket.OPEN) {
        return;
      }

      // Send heartbeat to server
      const heartbeat = {
        type: 'video_heartbeat',
        chunksRecorded: this.chunkIndex,
        lastChunkTime: this.lastChunkTime,
        timestamp: Date.now(),
      };

      this.config.websocket.send(JSON.stringify(heartbeat));
    }, VideoRecorder.HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Stop recording and flush remaining data. Resolves after the final
   * dataavailable flush has been sent (or buffered), so callers can safely
   * close the WebSocket afterwards. Browsers like Safari deliver the final
   * dataavailable asynchronously after stop(); a safety timeout keeps a
   * misbehaving browser from hanging teardown.
   */
  async stop(): Promise<void> {
    this.stopHeartbeat();

    const recorder = this.recorder;
    if (recorder && recorder.state !== 'inactive') {
      // Request final data before stopping
      if (recorder.state === 'recording') {
        recorder.requestData();
      }

      const stopped = new Promise<void>((resolve) => {
        this.notifyStopped = resolve;
      });
      recorder.stop();

      // The stop event fires after the final dataavailable per spec, so once
      // it lands the last chunk has reached handleChunk.
      const timedOut = await new Promise<boolean>((resolve) => {
        const timer = setTimeout(() => resolve(true), VideoRecorder.STOP_FLUSH_TIMEOUT_MS);
        void stopped.then(() => {
          clearTimeout(timer);
          resolve(false);
        });
      });
      this.notifyStopped = null;
      if (timedOut) {
        captureException(
          new Error('Timed out waiting for final MediaRecorder flush'),
          'VideoRecorderService.stop',
          { chunkIndex: this.chunkIndex, mimeType: this.actualMimeType },
        );
      }
    }

    await this.waitForInflightSends();

    // Last chance for chunks buffered during a disconnect: if the socket is
    // usable again, push them out before the caller closes it.
    if (this.pendingChunks.length > 0) {
      await this.flushPendingChunks();
    }
  }

  /**
   * Pause recording (keeps MediaRecorder active)
   */
  pause(): void {
    if (this.recorder?.state === 'recording') {
      this.recorder.pause();
      console.log('[VideoRecorder] Recording paused');
    }
  }

  /**
   * Resume recording after pause
   */
  resume(): void {
    if (this.recorder?.state === 'paused') {
      this.recorder.resume();
      console.log('[VideoRecorder] Recording resumed');
    }
  }

  /**
   * Request immediate data (useful before page unload)
   */
  requestData(): void {
    if (this.recorder?.state === 'recording') {
      this.recorder.requestData();
    }
  }

  /**
   * Clean up resources. Awaits the final flush (see stop) before clearing
   * config, so a late dataavailable still finds a live config to send through.
   */
  async cleanup(): Promise<void> {
    await this.stop();
    this.stopHeartbeat();
    this.recorder = null;
    this.config = null;
    this.isRecording = false;
    this.chunkIndex = 0;
    this.pendingChunks = [];
  }
}

// Export singleton-like factory for easy use
export function createVideoRecorder(): VideoRecorder {
  return new VideoRecorder();
}
